import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

import * as api from "../api.js";
import { PageHeader, ErrorBanner } from "../components.jsx";

const CATEGORY_OPTIONS = [
  { id: "medical", label: "Medical" },
  { id: "surgical", label: "Surgical" },
  { id: "diagnostic", label: "Diagnostic" },
  { id: "allied", label: "Allied Health" },
  { id: "other", label: "Other" },
];

function slugify(s) {
  return s
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");
}

export default function LibrarySpecialtyNew() {
  const nav = useNavigate();
  const [form, setForm] = useState({
    id: "",
    name: "",
    description: "",
    category: "medical",
    icon: "",
    color: "#455A64",
    body_systems: "",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const effectiveId = slugify(form.id || form.name);

  const save = async () => {
    setError(null);
    if (!form.name.trim()) {
      setError(new Error("Name is required"));
      return;
    }
    if (!effectiveId) {
      setError(new Error("ID is required (provide an ID or a name to derive one)"));
      return;
    }
    setBusy(true);
    try {
      await api.createSpecialtyCatalogEntry({
        id: effectiveId,
        name: form.name.trim(),
        description: form.description.trim(),
        category: form.category,
        icon: form.icon.trim() || form.name.trim().charAt(0).toUpperCase(),
        color: form.color,
        body_systems: form.body_systems
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean),
      });
      nav(`/library/draft/specialties/${encodeURIComponent(effectiveId)}`);
    } catch (err) {
      setError(err);
      setBusy(false);
    }
  };

  return (
    <>
      <PageHeader
        title="Add Specialty"
        subtitle="Add a new clinical specialty to the catalog."
        breadcrumbs={[
          { label: "Library", to: "/library/draft/platform" },
          { label: "Agent Groups", to: "/library/draft/specialties" },
          { label: "New" },
        ]}
        actions={
          <div className="btn-row">
            <button className="btn" onClick={() => nav(-1)} disabled={busy}>
              Cancel
            </button>
            <button className="btn primary" onClick={save} disabled={busy}>
              {busy ? "Saving..." : "Create"}
            </button>
          </div>
        }
      />

      <div className="page-body">
        <ErrorBanner error={error} />

        <div className="card">
          <div className="card-header">
            <h3>Identity</h3>
          </div>

          <div className="form-field">
            <label>Name</label>
            <input
              type="text"
              placeholder="e.g. Neurology"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            />
          </div>

          <div className="form-field">
            <label>Specialty ID (slug)</label>
            <input
              type="text"
              placeholder="auto-derived from name if blank"
              value={form.id}
              onChange={(e) => setForm((f) => ({ ...f, id: e.target.value }))}
            />
            <div className="help">
              Effective ID: <span className="mono">{effectiveId || "—"}</span>
            </div>
          </div>

          <div className="form-field">
            <label>Description</label>
            <input
              type="text"
              placeholder="Short description shown in the catalog grid"
              value={form.description}
              onChange={(e) =>
                setForm((f) => ({ ...f, description: e.target.value }))
              }
            />
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <h3>Classification</h3>
          </div>

          <div className="form-field">
            <label>Category</label>
            <select
              value={form.category}
              onChange={(e) =>
                setForm((f) => ({ ...f, category: e.target.value }))
              }
            >
              {CATEGORY_OPTIONS.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.label}
                </option>
              ))}
            </select>
          </div>

          <div style={{ display: "flex", gap: 16 }}>
            <div className="form-field" style={{ flex: 1 }}>
              <label>Icon (single char)</label>
              <input
                type="text"
                placeholder="e.g. N"
                maxLength={2}
                value={form.icon}
                onChange={(e) => setForm((f) => ({ ...f, icon: e.target.value }))}
              />
              <div className="help">
                Defaults to the first letter of the name if blank.
              </div>
            </div>
            <div className="form-field" style={{ flex: 1 }}>
              <label>Color</label>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <input
                  type="color"
                  value={form.color}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, color: e.target.value }))
                  }
                  style={{
                    width: 44,
                    height: 36,
                    padding: 0,
                    border: "1px solid var(--color-border)",
                    borderRadius: 4,
                  }}
                />
                <input
                  type="text"
                  value={form.color}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, color: e.target.value }))
                  }
                  style={{ flex: 1 }}
                />
              </div>
            </div>
          </div>

          <div className="form-field">
            <label>Body systems (comma separated)</label>
            <input
              type="text"
              placeholder="e.g. cardiovascular, respiratory"
              value={form.body_systems}
              onChange={(e) =>
                setForm((f) => ({ ...f, body_systems: e.target.value }))
              }
            />
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <h3>Preview</h3>
          </div>
          <div
            className="specialty-card"
            style={{ cursor: "default", maxWidth: 320 }}
          >
            <div
              className="tile"
              style={{ background: form.color }}
            >
              {form.icon || form.name.charAt(0).toUpperCase() || "?"}
            </div>
            <div className="body">
              <div className="name">{form.name || "(name)"}</div>
              {form.description && <div className="desc">{form.description}</div>}
              <div className="meta">
                <span className="badge badge-accent">{form.category}</span>
                <span className="muted small mono">{effectiveId || "—"}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
